// ChangePasswordModal.jsx
import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import api from '../../axiosConfig';
import Modal from 'bootstrap/js/dist/modal';

export default function ChangePasswordModal({
  showModal,     // boolean: controla visibilidad
  onClose,       // function(): callback al cerrar
  userId,        // number: id del usuario en sesión
}) {
  const modalId = 'modalCambioPass';
  const [pass1, setPass1] = useState('');
  const [pass2, setPass2] = useState('');
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState({ type: '', msg: '' });
  const modalRef = useRef(null);
  const bsInstanceRef = useRef(null);

  const mismatch = useMemo(() => pass1.trim() === '' || pass1 !== pass2, [pass1, pass2]);

  const resetForm = () => {
    setPass1('');
    setPass2('');
    setShow1(false);
    setShow2(false);
    setSubmitting(false);
    setAlert({ type: '', msg: '' });
  };

  // Inicializa y sincroniza el estado visible del modal con showModal
  useEffect(() => {
    const el = modalRef.current;
    if (!el) return;
    // Crear instancia si no existe
    if (!bsInstanceRef.current) {
      bsInstanceRef.current = new Modal(el, {
        backdrop: 'static',
        keyboard: false,
      });
      // Cuando se cierra, limpiar y notificar
      el.addEventListener('hidden.bs.modal', () => {
        resetForm();
        onClose && onClose();
      });
    }
    const inst = bsInstanceRef.current;
    if (showModal) {
      inst.show();
    } else {
      inst.hide();
    }
  }, [showModal, onClose]);

  const handleCloseClick = () => {
    if (bsInstanceRef.current) bsInstanceRef.current.hide();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlert({ type: '', msg: '' });

    if (!userId) {
      setAlert({ type: 'danger', msg: 'No se encontró el usuario en sesión.' });
      return;
    }
    if (mismatch) {
      setAlert({ type: 'danger', msg: 'Las contraseñas no coinciden o están vacías.' });
      return;
    }
    if (pass1.length < 8) {
      setAlert({ type: 'danger', msg: 'La contraseña debe tener al menos 8 caracteres.' });
      return;
    }

    setSubmitting(true);
    try {
      await api.put('/usuarios/cambioPass', {
        idUsuario: Number(userId),
        contrasenia: pass1,
      });
      setAlert({ type: 'success', msg: 'Contraseña actualizada correctamente.' });
      // Cerrar tras breve confirmación visual
      setTimeout(() => {
        if (bsInstanceRef.current) bsInstanceRef.current.hide();
      }, 900);
    } catch (err) {
      setAlert({
        type: 'danger',
        msg: err?.response?.data?.message || 'No se pudo actualizar la contraseña.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="modal fade"
      id={modalId}
      tabIndex={-1}
      aria-hidden="true"
      ref={modalRef}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content radius-16 bg-base">
          <div className="modal-header py-16 px-24 border-bottom">
            <h5 className="modal-title">Cambiar contraseña</h5>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={handleCloseClick}
            />
          </div>

          <div className="modal-body p-24">
            {alert.msg && (
              <div className={`alert alert-${alert.type} mb-16`} role="alert">
                {alert.msg}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* Nueva contraseña */}
                <div className="col-12 col-md-6 mb-20">
                  <label className="form-label">Nueva contraseña *</label>
                  <div className="input-group">
                    <input
                      type={show1 ? 'text' : 'password'}
                      className="form-control"
                      value={pass1}
                      onChange={(e) => setPass1(e.target.value)}
                      required
                      minLength={8}
                      placeholder="Mínimo 8 caracteres"
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShow1((s) => !s)}
                      title={show1 ? 'Ocultar' : 'Mostrar'}
                    >
                      <Icon icon={show1 ? 'mdi:eye-off-outline' : 'mdi:eye-outline'} />
                    </button>
                  </div>
                </div>

                {/* Confirmación */}
                <div className="col-12 col-md-6 mb-20">
                  <label className="form-label">Confirmar contraseña *</label>
                  <div className="input-group">
                    <input
                      type={show2 ? 'text' : 'password'}
                      className={`form-control ${mismatch && pass2 ? 'is-invalid' : ''}`}
                      value={pass2}
                      onChange={(e) => setPass2(e.target.value)}
                      required
                      minLength={8}
                      placeholder="Repite la contraseña"
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShow2((s) => !s)}
                      title={show2 ? 'Ocultar' : 'Mostrar'}
                    >
                      <Icon icon={show2 ? 'mdi:eye-off-outline' : 'mdi:eye-outline'} />
                    </button>
                    {mismatch && pass2 && (
                      <div className="invalid-feedback">Las contraseñas no coinciden.</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="text-center mt-24">
                <button
                  type="submit"
                  className="btn btn-primary px-48 py-12"
                  disabled={submitting || mismatch} >
                  {submitting ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </form>

            <div className="mt-16 small text-muted">
              Recomendación: al menos 8 caracteres; combina mayúsculas, minúsculas y números.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}